import Dexie from 'dexie';
import {offlineDb} from 'src/offline/db';
import type {EnqueueApplyLocalContext} from 'src/offline/outbox';
import type {
  AddCustomDeviceApplierArgs,
  AppendAttachmentArgs,
  RecordDeviceApplierArgs,
  TransitionApplierArgs,
} from './appliers';
import {
  appendAttachmentToEntry,
  applyAddCustomDevice,
  applyRecordDevice,
  applyTransition,
} from './appliers';
import {INSPECTION_OUTBOX_KINDS} from './executors';
import {
  baseSnapshotKey,
  inspectionEntryKeyPrefix,
  inspectionSnapshotKeys,
} from './snapshotKeys';

const mutableSnapshotKeys = (inspectionId: number): string[] => [
  inspectionSnapshotKeys.meta(inspectionId),
  inspectionSnapshotKeys.devices(inspectionId),
];

// Captures pristine copies of the snapshots that appliers mutate so state can be
// rebuilt by replay after a discard. Idempotent; skips keys already captured.
export const storeBaseSnapshots = async (
  sessionId: string,
  inspectionId: number
): Promise<void> => {
  for (const key of mutableSnapshotKeys(inspectionId)) {
    const baseKey = baseSnapshotKey(key);
    if (await offlineDb.snapshots.get([sessionId, baseKey])) continue;
    const snap = await offlineDb.snapshots.get([sessionId, key]);
    if (!snap) continue;
    await offlineDb.snapshots.put({
      key: baseKey,
      sessionId,
      data: snap.data,
      cachedAt: new Date().toISOString(),
    });
  }
};

const replayItem = async (
  kind: string,
  payload: unknown,
  ctx: EnqueueApplyLocalContext
): Promise<void> => {
  switch (kind) {
    case INSPECTION_OUTBOX_KINDS.recordDevice:
      return applyRecordDevice(payload as RecordDeviceApplierArgs, ctx);
    case INSPECTION_OUTBOX_KINDS.addCustomDevice:
      return applyAddCustomDevice(payload as AddCustomDeviceApplierArgs, ctx);
    case INSPECTION_OUTBOX_KINDS.addAttachment:
      // Blob already stored at capture time; only re-append the snapshot ref.
      return appendAttachmentToEntry(payload as AppendAttachmentArgs, ctx);
    case INSPECTION_OUTBOX_KINDS.transition:
      return applyTransition(payload as TransitionApplierArgs, ctx);
    default:
      return undefined;
  }
};

// Rebuilds local inspection state from the download-time base snapshots plus a
// replay of the remaining outbox items, so discards no longer leave stale state.
export const rebuildLocalInspectionState = async (
  sessionId: string,
  inspectionId: number
): Promise<boolean> => {
  const metaKey = inspectionSnapshotKeys.meta(inspectionId);
  const devicesKey = inspectionSnapshotKeys.devices(inspectionId);
  const baseMeta = await offlineDb.snapshots.get([
    sessionId,
    baseSnapshotKey(metaKey),
  ]);
  const baseDevices = await offlineDb.snapshots.get([
    sessionId,
    baseSnapshotKey(devicesKey),
  ]);
  if (!baseMeta || !baseDevices) return false;

  const now = new Date().toISOString();
  await offlineDb.snapshots.put({
    key: metaKey,
    sessionId,
    data: baseMeta.data,
    cachedAt: now,
  });
  await offlineDb.snapshots.put({
    key: devicesKey,
    sessionId,
    data: baseDevices.data,
    cachedAt: now,
  });

  const entryPrefix = inspectionEntryKeyPrefix(inspectionId);
  const entrySnaps = await offlineDb.snapshots
    .where('sessionId')
    .equals(sessionId)
    .filter(snap => snap.key.startsWith(entryPrefix))
    .toArray();
  await Promise.all(
    entrySnaps.map(snap => offlineDb.snapshots.delete([sessionId, snap.key]))
  );

  const items = await offlineDb.outbox
    .where('[sessionId+seq]')
    .between([sessionId, Dexie.minKey], [sessionId, Dexie.maxKey])
    .sortBy('seq');
  for (const item of items) {
    await replayItem(item.kind, item.payload, {
      sessionId,
      clientSubmissionId: item.clientSubmissionId,
    });
  }
  return true;
};
